<template>
  <div class="max-w-md w-full mx-auto px-4 py-6 space-y-6 pb-20">
    <!-- Header -->
    <header class="flex justify-between items-center bg-slate-900/50 backdrop-blur-md py-4 px-1 rounded-xl">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center font-bold text-white shadow-lg shadow-indigo-600/30">
          R
        </div>
        <div>
          <h1 class="text-base font-bold text-slate-100 leading-tight">Rameria Pass</h1>
          <p class="text-xs text-slate-400 font-medium">{{ store.event?.name || 'Loading event...' }}</p>
        </div>
      </div>
      <button 
        @click="handleLogout"
        class="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-800 text-slate-400 hover:bg-slate-800 hover:text-slate-100 transition-colors"
      >
        Sign Out
      </button>
    </header>

    <!-- Profile Summary -->
    <div class="pwa-card p-5 relative overflow-hidden">
      <div class="absolute top-0 right-0 w-24 h-24 bg-indigo-500/10 rounded-full blur-2xl -z-10"></div>
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center font-bold text-indigo-400 text-lg">
          {{ store.person?.name?.charAt(0).toUpperCase() || 'P' }}
        </div>
        <div>
          <h3 class="text-lg font-bold text-slate-100">{{ store.person?.name }}</h3>
          <p class="text-xs text-slate-400 font-medium">+{{ store.person?.whatsapp_number }}</p>
        </div>
      </div>
    </div>

    <!-- Ticketing Flow (Task 9.6) -->
    <div v-if="features.enable_ticketing">
      <!-- 1. Loading Ticket Info -->
      <div v-if="loadingTicket" class="pwa-card p-6 flex flex-col items-center justify-center space-y-3">
        <svg class="h-6 w-6 animate-spin text-indigo-500" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z"/>
        </svg>
        <span class="text-xs text-slate-400">Loading ticket status...</span>
      </div>

      <!-- 2. No Ticket / Buy Ticket Tier -->
      <div v-else-if="!ticketInfo" class="pwa-card p-6 bg-gradient-to-br from-slate-900 to-indigo-950/30 border-indigo-500/10 space-y-4">
        <div class="flex justify-between items-start">
          <div>
            <span class="text-[10px] font-bold text-indigo-400 uppercase tracking-widest bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-500/20">
              Ticket Tier Selection
            </span>
            <h3 class="text-lg font-bold text-slate-100 mt-2">Get Your Entrance Ticket</h3>
          </div>
        </div>

        <div v-if="activeTier" class="bg-slate-950/40 p-4 rounded-xl border border-slate-800/80 space-y-3">
          <div class="flex justify-between items-center">
            <div>
              <span class="text-sm font-bold text-slate-200">{{ activeTier.name }}</span>
              <span class="text-[10px] text-slate-400 block">Active Pricing Tier</span>
            </div>
            <span class="text-base font-extrabold text-indigo-400">
              Rp {{ formatCurrency(activeTier.price) }}
            </span>
          </div>

          <div class="flex justify-between items-center text-xs text-slate-400">
            <span>Remaining Quota:</span>
            <span class="font-bold text-slate-200">{{ activeTier.quota - activeTier.sold_count }} left</span>
          </div>
        </div>
        <div v-else class="text-xs text-slate-400 bg-slate-950/40 p-4 rounded-xl text-center border border-slate-800/80">
          No active ticket tier is available right now. Please check back later!
        </div>

        <div v-if="activeTier" class="space-y-3">
          <div class="space-y-1.5">
            <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Metode Pembayaran:</span>
            <div class="p-3 rounded-xl border border-indigo-500/30 bg-indigo-500/5 flex items-center gap-3">
              <div class="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 4v1m6 11h2m-6 0h-2v4m0-11v3" />
                </svg>
              </div>
              <div>
                <span class="text-xs font-bold text-indigo-300">QRIS Mandiri Merchant</span>
                <span class="text-[9px] text-slate-400 block mt-0.5">Scan & pay dari semua bank / e-wallet</span>
              </div>
            </div>
          </div>

          <button
            @click="handleInitiatePayment"
            :disabled="initiatingPayment || activeTier.quota - activeTier.sold_count <= 0"
            class="pwa-btn-primary w-full py-2.5 font-bold text-xs"
          >
            <span v-if="initiatingPayment">Memproses...</span>
            <span v-else>Pesan & Bayar Sekarang</span>
          </button>
          <p v-if="paymentError" class="text-xs text-red-400 text-center">{{ paymentError }}</p>
        </div>
      </div>

      <!-- 3. Ticket exists, but payment is PENDING or FAILED -->
      <div v-else-if="ticketInfo && latestPaymentStatus !== 'SUCCESS'" class="pwa-card p-6 bg-gradient-to-br from-slate-900 to-amber-950/20 border-amber-500/20 space-y-4">
        <div>
          <span class="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
            Payment {{ latestPaymentStatus }}
          </span>
          <h3 class="text-lg font-bold text-slate-100 mt-2">Complete Your Payment</h3>
          <p class="text-xs text-slate-400 mt-1">Your slot has been reserved. Please complete your transaction to receive your E-Boarding Pass.</p>
        </div>

        <div class="bg-slate-950/40 p-4 rounded-xl border border-slate-800/80 flex justify-between items-center">
          <div>
            <span class="text-xs text-slate-400">Amount Due</span>
            <span class="text-lg font-extrabold text-slate-200 block">Rp {{ formatCurrency(latestPaymentAmount) }}</span>
          </div>
          <span class="text-xs text-amber-600 dark:text-amber-400 font-medium">Pending status</span>
        </div>

        <div class="space-y-4">
          <!-- QRIS Dynamic Display (Mandiri Merchant) -->
          <div v-if="latestPaymentMethod === 'qris'" class="flex flex-col items-center justify-center space-y-3">
            <!-- Mandiri QRIS header -->
            <div class="flex items-center gap-2 w-full">
              <div class="w-6 h-6 rounded bg-amber-500/20 flex items-center justify-center">
                <svg class="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 4v1" />
                </svg>
              </div>
              <span class="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">QRIS Mandiri Merchant</span>
            </div>

            <!-- Warning: exact amount -->
            <div class="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-left w-full space-y-1">
              <span class="text-[10px] font-bold text-amber-600 dark:text-amber-400 block uppercase tracking-wider">⚠️ Transfer Nominal Tepat</span>
              <p class="text-[10px] text-amber-800 dark:text-amber-200 leading-relaxed">
                Transfer tepat <strong class="text-slate-900 dark:text-slate-100 font-extrabold text-xs">Rp {{ formatCurrency(latestPaymentAmount) }}</strong> (sudah termasuk 3 digit kode unik). Jangan dibulatkan - sistem akan mencocokkan nominal secara otomatis dari notifikasi Livin' Mandiri.
              </p>
            </div>

            <!-- QRIS QR Code -->
            <div class="bg-white p-4 rounded-2xl shadow-xl max-w-[200px] mx-auto border border-slate-200">
              <img
                :src="qrisCodeUrl"
                alt="QRIS Mandiri Merchant"
                class="w-40 h-40 object-contain rounded-lg"
              />
            </div>
            <div class="flex gap-2 w-full max-w-[320px] justify-center px-4">
              <button
                @click="downloadQrCode(qrisCodeUrl, 'qris_payment.png')"
                type="button"
                class="pwa-btn-secondary flex-1 text-center text-[10px] font-bold py-2"
              >
                Simpan ke Galeri
              </button>
              <button
                @click="copyQrisString(latestPayment.qris_string)"
                type="button"
                class="pwa-btn-secondary flex-1 text-center text-[10px] font-bold py-2"
              >
                {{ qrisCopied ? '✓ Tersalin!' : 'Salin Teks QRIS' }}
              </button>
            </div>
            <p class="text-[10px] text-slate-400 text-center px-4">
              Scan menggunakan Livin' Mandiri, BCA, GoPay, OVO, Dana, atau aplikasi perbankan lainnya.
            </p>
            <p class="text-[10px] text-slate-500 text-center">
              Status akan otomatis terupdate setelah pembayaran terdeteksi.
            </p>
          </div>

          <!-- Fallback -->
          <div v-else class="p-4 rounded-xl border border-slate-800 text-center text-xs text-slate-400">
            Metode pembayaran tidak dikenali. Hubungi panitia.
          </div>

          <!-- Dev: Simulate Settlement -->
          <button
            @click="simulatePaymentSuccess"
            class="pwa-btn-secondary w-full py-2 font-bold text-[10px] tracking-wider uppercase border-dashed border-indigo-500/30 text-indigo-400 hover:text-indigo-300"
          >
            Simulate QRIS Settlement (Dev)
          </button>
        </div>
      </div>

      <!-- 4. Ticket exists and is PAID/SUCCESS (Render E-Boarding Pass) -->
      <div v-else-if="ticketInfo && latestPaymentStatus === 'SUCCESS'" class="pwa-card bg-gradient-to-br from-slate-900 to-indigo-950/40 p-6 border-indigo-500/20 relative overflow-hidden">
        <!-- Decorative Elements -->
        <div class="absolute -top-12 -left-12 w-24 h-24 rounded-full border border-indigo-500/10"></div>
        <div class="absolute -bottom-12 -right-12 w-24 h-24 rounded-full border border-indigo-500/10"></div>

        <div class="flex justify-between items-start mb-6">
          <div>
            <span class="text-[10px] font-bold text-indigo-400 uppercase tracking-widest bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-500/20">
              E-Boarding Pass
            </span>
            <h4 class="text-xs text-slate-400 mt-2 font-medium">REGISTRATION ID</h4>
            <p class="text-sm font-bold text-slate-200 tracking-wider">
              {{ store.registration?.id.substring(0, 8).toUpperCase() }}-{{ store.registration?.id.substring(9, 13).toUpperCase() }}
            </p>
          </div>
          <div class="text-right">
            <h4 class="text-xs text-slate-400 font-medium">SHIRT SIZE</h4>
            <p class="text-sm font-bold text-slate-200">{{ store.registration?.shirt_size || 'N/A' }}</p>
          </div>
        </div>

        <!-- QR Code -->
        <div class="flex flex-col items-center justify-center bg-white p-4 rounded-xl max-w-[200px] mx-auto shadow-xl">
          <img
            :src="qrCodeUrl"
            alt="Ticket QR Code"
            class="w-40 h-40 object-contain rounded-lg"
          />
          <span class="text-[10px] font-bold text-slate-800 tracking-widest mt-3 uppercase">
            SCAN AT CHECK-IN
          </span>
        </div>

        <div class="mt-6">
          <button
            @click="downloadQrCode(qrCodeUrl, 'boarding_pass.png')"
            type="button"
            class="pwa-btn-secondary w-full text-center text-xs font-semibold py-2 block"
          >
            Save QR to Gallery
          </button>
        </div>
      </div>
    </div>

    <!-- Referral Hub -->
    <div v-if="features.enable_referrals" class="pwa-card p-5 space-y-4">
      <div class="flex justify-between items-center pb-3 border-b border-slate-800">
        <h3 class="text-sm font-bold text-slate-100 flex items-center gap-2">
          <svg class="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Referral Program
        </h3>
        <span class="text-xs text-slate-400 font-medium">Wallet Balance</span>
      </div>

      <div class="grid grid-cols-2 gap-4 bg-slate-950/40 p-4 rounded-xl border border-slate-800/60">
        <div>
          <span class="text-xs text-slate-400 font-medium block mb-1">Referral Code</span>
          <span class="text-lg font-extrabold text-indigo-400 tracking-wide">
            {{ store.registration?.referral_code || 'Loading...' }}
          </span>
        </div>
        <div class="text-right">
          <span class="text-xs text-slate-400 font-medium block mb-1">Commission Earned</span>
          <span class="text-lg font-extrabold text-emerald-400">
            IDR {{ formatCurrency(store.registration?.wallet_balance || 0) }}
          </span>
        </div>
      </div>

      <div class="space-y-2">
        <button @click="shareReferral" class="pwa-btn-primary w-full text-xs font-bold py-2">
          {{ copied ? '✓ Link Copied!' : 'Share Referral Link' }}
        </button>
        <button @click="openWallet" class="pwa-btn-secondary w-full text-xs font-semibold py-2">
          Open Wallet & Cash-Out
        </button>
        <p class="text-[10px] text-slate-500 text-center">
          Share your referral link with friends. You earn commissions when they complete registration.
        </p>
      </div>
    </div>

    <!-- Quests Hub -->
    <div v-if="features.enable_quests" class="pwa-card p-5 space-y-4">
      <div class="flex justify-between items-center pb-3 border-b border-slate-800">
        <h3 class="text-sm font-bold text-slate-100 flex items-center gap-2">
          <svg class="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
          Interactive Quests
        </h3>
        <span class="text-xs font-bold text-slate-400">
          {{ completedQuestsCount }} / {{ questsList.length }} Done
        </span>
      </div>

      <div v-if="questsList.length === 0" class="text-center py-6 text-xs text-slate-500">
        No active quests for this event.
      </div>
      <div v-else class="space-y-3">
        <div 
          v-for="q in questsList" 
          :key="q.id"
          class="p-3 bg-slate-950/40 border border-slate-800/80 rounded-xl flex items-center justify-between"
        >
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-xs text-indigo-400 font-bold">
              +{{ q.points_value }}
            </div>
            <div>
              <h4 class="text-xs font-bold text-slate-200">
                {{ q.name }}
              </h4>
              <p class="text-[10px] text-slate-400 capitalize">
                Category: {{ q.category.replace('_', ' ').toLowerCase() }}
              </p>
            </div>
          </div>
          <div>
            <span 
              v-if="q.completion_status" 
              class="text-[9px] font-extrabold px-1.5 py-0.5 rounded uppercase tracking-wider"
              :class="{
                'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20': q.completion_status === 'APPROVED',
                'bg-amber-500/10 text-amber-400 border border-amber-500/20': q.completion_status === 'PENDING',
                'bg-red-500/10 text-red-400 border border-red-500/20': q.completion_status === 'REJECTED',
              }"
            >
              {{ q.completion_status }}
            </span>
            <span v-else class="text-[9px] font-bold text-slate-600 uppercase">
              NOT STARTED
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useParticipantStore } from '../stores/participant'

const router = useRouter()
const route = useRoute()
const store = useParticipantStore()

const copied = ref(false)
const qrisCopied = ref(false)

async function copyQrisString(text: string) {
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    qrisCopied.value = true
    setTimeout(() => {
      qrisCopied.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy QRIS string', err)
  }
}

const features = computed(() => {
  return store.event?.config?.features || {
    enable_quests: true,
    enable_referrals: true,
    enable_ticketing: true,
  }
})

const loadingTicket = ref(false)
const ticketInfo = ref<any>(null)
const activeTier = ref<any>(null)
const initiatingPayment = ref(false)
const paymentError = ref<string | null>(null)

const API_BASE = (import.meta as any).env.VITE_API_BASE || '/api'

const latestPayment = computed(() => {
  if (!ticketInfo.value || !ticketInfo.value.payments || ticketInfo.value.payments.length === 0) return null
  const sorted = [...ticketInfo.value.payments].sort((a: any, b: any) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
  return sorted[0]
})

const latestPaymentStatus = computed(() => {
  return latestPayment.value ? latestPayment.value.status : 'NONE'
})

const latestPaymentMethod = computed(() => {
  return latestPayment.value ? latestPayment.value.method : 'NONE'
})

const latestPaymentAmount = computed(() => {
  return latestPayment.value ? latestPayment.value.amount : 0
})

const qrisCodeUrl = computed(() => {
  if (!latestPayment.value || !latestPayment.value.qris_string) return ''
  return `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(latestPayment.value.qris_string)}`
})

const qrCodeUrl = computed(() => {
  if (!ticketInfo.value || !ticketInfo.value.ticket) return ''
  return `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(ticketInfo.value.ticket.qr_secret)}`
})

async function fetchTicketStatus() {
  if (!store.registration?.id) return
  loadingTicket.value = true
  try {
    const res = await fetch(`${API_BASE}/registrations/${store.registration.id}/ticket`)
    if (res.ok) {
      ticketInfo.value = await res.json()
    } else {
      ticketInfo.value = null
    }
  } catch (error) {
    console.error('Error fetching ticket status', error)
    ticketInfo.value = null
  } finally {
    loadingTicket.value = false
  }
}

// Dynamic Payment Settlement Notification Listener & Polling (Task 9.3)
let pollingInterval: any = null

function startPaymentPolling() {
  if (pollingInterval) return
  pollingInterval = setInterval(async () => {
    if (latestPaymentStatus.value !== 'PENDING') {
      stopPaymentPolling()
      return
    }
    // Silently fetch ticket status in background to update UI on success
    try {
      const res = await fetch(`${API_BASE}/registrations/${store.registration?.id}/ticket`)
      if (res.ok) {
        const info = await res.json()
        const sorted = info.payments && info.payments.length > 0
          ? [...info.payments].sort((a: any, b: any) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
          : []
        const newLatest = sorted[0]
        
        if (newLatest && newLatest.status === 'SUCCESS') {
          ticketInfo.value = info
          stopPaymentPolling()
        } else {
          ticketInfo.value = info
        }
      }
    } catch (err) {
      console.error('Payment polling check failed', err)
    }
  }, 4000)
}

function stopPaymentPolling() {
  if (pollingInterval) {
    clearInterval(pollingInterval)
    pollingInterval = null
  }
}

watch(latestPaymentStatus, (newStatus) => {
  if (newStatus === 'PENDING') {
    startPaymentPolling()
  } else {
    stopPaymentPolling()
  }
}, { immediate: true })

onUnmounted(() => {
  stopPaymentPolling()
})

async function fetchActiveTiers() {
  if (!store.event?.slug) return
  try {
    const res = await fetch(`${API_BASE}/events/${store.event.slug}/ticket-tiers`)
    if (res.ok) {
      const tiers = await res.json()
      const now = new Date()
      const activeTiers = tiers.filter((t: any) => t.is_active && new Date(t.starts_at) <= now && new Date(t.ends_at) >= now)
      activeTier.value = activeTiers[0] || null
    }
  } catch (error) {
    console.error('Error fetching active tiers', error)
  }
}

async function handleInitiatePayment() {
  if (!store.registration?.id) return
  initiatingPayment.value = true
  paymentError.value = null
  try {
    const res = await fetch(`${API_BASE}/registrations/${store.registration.id}/payments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ method: 'qris' }),
    })
    const data = await res.json()
    if (res.ok) {
      await fetchTicketStatus()
    } else {
      paymentError.value = data.error || 'Gagal memulai pembayaran'
    }
  } catch (err: any) {
    paymentError.value = err.message || 'Connection error'
  } finally {
    initiatingPayment.value = false
  }
}

async function simulatePaymentSuccess() {
  const payment = latestPayment.value
  if (!payment) return
  
  try {
    const res = await fetch(`${API_BASE}/webhooks/qris-settle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ payment_id: payment.id }),
    })
    if (res.ok) {
      await fetchTicketStatus()
    } else {
      const data = await res.json()
      alert(data.error || 'Simulate settlement failed')
    }
  } catch (error) {
    console.error('Simulate settlement error', error)
  }
}

const questsList = ref<any[]>([])
const loadingQuests = ref(false)

const completedQuestsCount = computed(() => {
  return questsList.value.filter((q: any) => q.completion_status === 'APPROVED').length
})

async function fetchQuests() {
  if (!store.event?.slug || !store.registration?.id) return
  loadingQuests.value = true
  try {
    const res = await fetch(`${API_BASE}/events/${store.event.slug}/quests?registration_id=${store.registration.id}`)
    if (res.ok) {
      questsList.value = await res.json()
    }
  } catch (error) {
    console.error('Failed to load quests', error)
  } finally {
    loadingQuests.value = false
  }
}

onMounted(async () => {
  const slug = route.params.slug as string
  await store.loadEventContext(slug)
  await store.fetchSessionProfile()
  await fetchTicketStatus()
  await fetchActiveTiers()
  await fetchQuests()
})

function formatCurrency(value: number): string {
  return value.toLocaleString('id-ID')
}

async function downloadQrCode(url: string, filename: string) {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 600
    canvas.height = 850
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not create canvas context')

    if (filename === 'boarding_pass.png') {
      // 1. Draw premium background gradient (dark premium card style)
      const gradient = ctx.createLinearGradient(0, 0, 0, 850)
      gradient.addColorStop(0, '#0f172a') // slate-900
      gradient.addColorStop(1, '#020617') // slate-950
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, 600, 850)

      // Decorative circles
      ctx.strokeStyle = 'rgba(99, 102, 241, 0.15)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.arc(300, 425, 270, 0, Math.PI * 2)
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(300, 425, 220, 0, Math.PI * 2)
      ctx.stroke()

      // Header
      ctx.fillStyle = '#818cf8' // indigo-400
      ctx.font = 'bold 16px Satoshi, sans-serif'
      ctx.fillText('RAMERIA PASS', 50, 60)

      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 28px sans-serif'
      ctx.fillText(store.event?.name || 'Merdeka Roll-In 2026', 50, 100)

      // Separator
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(50, 130)
      ctx.lineTo(550, 130)
      ctx.stroke()

      // Participant Info
      ctx.fillStyle = '#94a3b8'
      ctx.font = '500 14px Satoshi, sans-serif'
      ctx.fillText('PARTICIPANT NAME', 50, 170)
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 20px Satoshi, sans-serif'
      ctx.fillText(store.person?.name || 'Rider Rameria', 50, 200)

      ctx.fillStyle = '#94a3b8'
      ctx.font = '500 14px Satoshi, sans-serif'
      ctx.fillText('REGISTRATION ID', 50, 250)
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 20px Satoshi, sans-serif'
      const regId = store.registration?.id || 'MOCK-REG-ID'
      const cleanRegId = regId.length > 15 
        ? `${regId.substring(0, 8).toUpperCase()}-${regId.substring(9, 13).toUpperCase()}`
        : regId.toUpperCase()
      ctx.fillText(cleanRegId, 50, 280)

      ctx.fillStyle = '#94a3b8'
      ctx.font = '500 14px Satoshi, sans-serif'
      ctx.fillText('SHIRT SIZE', 400, 250)
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 20px Satoshi, sans-serif'
      ctx.fillText(store.registration?.shirt_size || 'L', 400, 280)

      // QR Code loading
      const qrImage = new Image()
      qrImage.crossOrigin = 'anonymous'
      qrImage.src = url
      await new Promise((resolve, reject) => {
        qrImage.onload = resolve
        qrImage.onerror = reject
      })

      // White card behind QR
      ctx.fillStyle = '#ffffff'
      ctx.beginPath()
      if (typeof (ctx as any).roundRect === 'function') {
        (ctx as any).roundRect(150, 350, 300, 380, 24)
      } else {
        ctx.rect(150, 350, 300, 380)
      }
      ctx.fill()

      // Draw QR Code
      ctx.drawImage(qrImage, 180, 380, 240, 240)

      // Scan labels
      ctx.fillStyle = '#0f172a'
      ctx.font = 'bold 16px Satoshi, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('SCAN AT CHECK-IN', 300, 660)

      ctx.fillStyle = '#64748b'
      ctx.font = '500 11px Satoshi, sans-serif'
      ctx.fillText('Rameria Merdeka Roll-In 2026', 300, 690)

    } else if (filename === 'qris_payment.png') {
      // 1. Background gradient (payment/amber accent card style)
      const gradient = ctx.createLinearGradient(0, 0, 0, 850)
      gradient.addColorStop(0, '#1e1b4b') // indigo-950
      gradient.addColorStop(1, '#020617') // slate-950
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, 600, 850)

      // Header
      ctx.fillStyle = '#fbbf24' // amber-400
      ctx.font = 'bold 16px Satoshi, sans-serif'
      ctx.fillText('QRIS MANDIRI MERCHANT', 50, 60)

      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 28px sans-serif'
      ctx.fillText(store.event?.name || 'Merdeka Roll-In 2026', 50, 100)

      // Separator
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(50, 130)
      ctx.lineTo(550, 130)
      ctx.stroke()

      // Payment Details
      ctx.fillStyle = '#94a3b8'
      ctx.font = '500 14px Satoshi, sans-serif'
      ctx.fillText('TOTAL AMOUNT DUE', 50, 170)
      ctx.fillStyle = '#fbbf24'
      ctx.font = 'bold 32px Satoshi, sans-serif'
      ctx.fillText(`Rp ${latestPaymentAmount.value.toLocaleString('id-ID')}`, 50, 210)

      ctx.fillStyle = '#94a3b8'
      ctx.font = '500 14px Satoshi, sans-serif'
      ctx.fillText('REGISTRATION ID', 50, 260)
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 18px Satoshi, sans-serif'
      const regId = store.registration?.id || 'MOCK-REG-ID'
      ctx.fillText(regId.toUpperCase(), 50, 290)

      // QR Code loading
      const qrImage = new Image()
      qrImage.crossOrigin = 'anonymous'
      qrImage.src = url
      await new Promise((resolve, reject) => {
        qrImage.onload = resolve
        qrImage.onerror = reject
      })

      // White card behind QR
      ctx.fillStyle = '#ffffff'
      ctx.beginPath()
      if (typeof (ctx as any).roundRect === 'function') {
        (ctx as any).roundRect(150, 350, 300, 380, 24)
      } else {
        ctx.rect(150, 350, 300, 380)
      }
      ctx.fill()

      // Draw QR Code
      ctx.drawImage(qrImage, 180, 380, 240, 240)

      // Payment instructions on image
      ctx.fillStyle = '#0f172a'
      ctx.font = 'bold 16px Satoshi, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('SCAN TO PAY', 300, 660)

      ctx.fillStyle = '#64748b'
      ctx.font = '500 11px Satoshi, sans-serif'
      ctx.fillText('Transfer EXACT amount to auto-settle', 300, 690)

    } else {
      throw new Error('Defaulting to direct download')
    }

    // Trigger composed canvas download
    canvas.toBlob((blob) => {
      if (!blob) {
        fallbackDownload(url, filename)
        return
      }
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    }, 'image/png')

  } catch (error) {
    console.warn('Canvas download failed, falling back to direct QR code download:', error)
    await fallbackDownload(url, filename)
  }
}

async function fallbackDownload(url: string, filename: string) {
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('Direct download failed:', error)
    window.open(url, '_blank')
  }
}

async function shareReferral() {
  if (!store.registration?.referral_code) return
  const link = `${window.location.origin}/events/${store.event?.slug}/register?ref=${store.registration.referral_code}`
  
  const shareData = {
    title: `Join me at ${store.event?.name || 'Sentinel Event'}!`,
    text: `Register using my referral link to join the event:`,
    url: link,
  }

  if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
    try {
      await navigator.share(shareData)
    } catch (err) {
      console.log('Share cancelled or failed', err)
    }
  } else {
    // Fallback to copy link
    try {
      await navigator.clipboard.writeText(link)
      copied.value = true
      setTimeout(() => {
        copied.value = false
      }, 2000)
    } catch (err) {
      console.error('Failed to copy link', err)
    }
  }
}

function openWallet() {
  router.push({
    name: 'wallet',
    params: { slug: store.event?.slug || route.params.slug }
  })
}

function handleLogout() {
  store.logout()
  router.push({
    name: 'login',
    params: { slug: store.event?.slug || route.params.slug }
  })
}
</script>
